import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User, UserProfilePicture } from 'app/model/user';
import { Observable, of, throwError } from 'rxjs';
import { urls } from 'assets/url/urls';
import { catchError } from 'rxjs/operators';

@Injectable()
export class UserService {
  // Constante de la api de la base de datos
  //private REST_API_SERVER = 'https://json-portal-empleado.onrender.com';
  private REST_API_SERVER = urls.baseUrl;
  private TENANT = urls.tenant;


  headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Access-Control-Allow-Headers': 'Content-Type',
    'X-TENANT-ID': this.TENANT
  }
  requestOptions = {
    headers: new HttpHeaders(this.headers),
  };


  /**
   * Constructor
   * @param httpClient
   */
  constructor(private httpClient: HttpClient) { }

  handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }

  /**
   * Trae los usuarios de la base de datos
   * @returns Usuario
   */
  public getUser() {
    return this.httpClient.get(this.REST_API_SERVER + '/users', this.requestOptions);
  }

  public getUserFromCommunication(id) {
    return this.httpClient.get(this.REST_API_SERVER + '/userFromCommunications/' + id, this.requestOptions);
  }

  /**
   * Trae el usuario con la id seleccionada
   * @param id
   * @returns Usuario por id
   */
  public getUserById(id) {
    return this.httpClient.get(this.REST_API_SERVER + '/users/' + id, this.requestOptions);
  }

  public getUserByUsername(username) {
    return this.httpClient.get(this.REST_API_SERVER + '/users?username=' + username, this.requestOptions);
  }

  public getUserProfilePictureById(userId: number): Observable<Blob | null> {
    const headers = {
      'Accept': 'application/json',
      'Access-Control-Allow-Headers': 'Content-Type',
      'X-TENANT-ID': this.TENANT
    }

    const requestOptions = {
      headers: new HttpHeaders(headers),
      responseType: 'blob' as 'blob'
    }

    return this.httpClient.get(this.REST_API_SERVER + `/users/${userId}/profile_picture`, requestOptions)
      .pipe(
        catchError((err) => of(null))
      )
  }

  public getUserProfilePictureByEmployeeId(employeeId: number): Observable<Blob | null> {
    const headers = {
      'Accept': 'application/json',
      'Access-Control-Allow-Headers': 'Content-Type',
      'X-TENANT-ID': this.TENANT
    }

    const requestOptions = {
      headers: new HttpHeaders(headers),
      responseType: 'blob' as 'blob'
    }

    return this.httpClient.get(this.REST_API_SERVER + `/users/employees/${employeeId}/profile_picture`, requestOptions)
      .pipe(
        catchError((err) => of(null))
      )
  }

  public getUserProfilePicturesByUserIds(userIds: number[]): Observable<UserProfilePicture[]> {
    const headers = this.headers

    const params: HttpParams = new HttpParams()
      .set('user_ids', userIds.toString())

    return this.httpClient.get<UserProfilePicture[]>(this.REST_API_SERVER + `/users/profile_picture`, { headers, params })
  }

  public getUserProfilePicturesByEmployeeIds(employeeIds: number[]): Observable<UserProfilePicture[]> {
    const headers = this.headers

    const params: HttpParams = new HttpParams()
      .set('employee_ids', employeeIds.toString())

    return this.httpClient.get<UserProfilePicture[]>(this.REST_API_SERVER + `/users/employees/profile_picture`, { headers, params })
  }

  /**
   * Crea el usuario en la base de datos
   * @param user
   * @returns Usuario
   */
  public addUser(user: User) {
    return this.httpClient.post<User>(this.REST_API_SERVER + '/users', user, this.requestOptions);
  }

  /**
   * Actualiza el usuario con la id seleccionada en la base de datos
   * @param id
   * @param user
   * @param profilePicture?
   * @returns Usuario actualizado
   */
  public updateUserById(id, user: User, profilePicture?: File, cv?: File) {
    const formData = new FormData()
    formData.append(
      'user',
      new Blob([JSON.stringify(user)], { type: 'application/json' })
    )

    if (profilePicture) formData.append('profile_picture', profilePicture)

    if (cv) formData.append('cv', cv)

    const headers = {
      'Accept': 'application/json',
      'Access-Control-Allow-Headers': 'Content-Type',
      'X-TENANT-ID': this.TENANT
    }

    const requestOptions = {
      headers: new HttpHeaders(headers)
    }

    return this.httpClient.put(this.REST_API_SERVER + '/users/' + id, formData, requestOptions)
  }

  public changePassword(user: User, newPass: String) {
    return this.httpClient.put(this.REST_API_SERVER + '/change_password/' + user.id, newPass, this.requestOptions);
  }

  /**
   * Crea el usuario en la base de datos
   * @param user
   * @returns Usuario
   */
  public createUser(user: User, profilePicture?: File, cv?: File) {
    const formData = new FormData()
    formData.append(
      'user',
      new Blob([JSON.stringify(user)], { type: 'application/json' })
    )

    if (profilePicture) formData.append('profile_picture', profilePicture)

    if (cv) formData.append('cv', cv)

    const headers = {
      'Accept': 'application/json',
      'Access-Control-Allow-Headers': 'Content-Type',
      'X-TENANT-ID': this.TENANT
    }

    const requestOptions = {
      headers: new HttpHeaders(headers)
    }

    return this.httpClient.post(this.REST_API_SERVER + '/users/', formData, requestOptions);
  }

  /**
   * Borra el usuario de la base de datos
   * @param id
   * @returns Usuario
   */
  public deleteUserById(id) {
    return this.httpClient.delete(this.REST_API_SERVER + '/users/' + id, this.requestOptions);
  }

  /**
   * Upload Profile Picture
   */
  public uploadProfilePicture(formaData: FormData, id) {
    return this.httpClient.post(this.REST_API_SERVER + '/users/' + id, this.requestOptions);
  }
}
